import { inject, observer } from 'mobx-react';
import * as moment from 'moment';
import * as React from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { RouteComponentProps } from 'react-router';

import { StorySettingsConstants as ui } from '../../Config/UIConstants';
import { Tab } from '../../SharedComponents/CommonTabbedContainer';
import { ExitableAlert } from '../../SharedComponents/ExitableAlert';
import { FullContainer } from '../../SharedComponents/FullContainer';
import { FullScreenModalContainer } from '../../SharedComponents/FullScreenModalContainer';
import { ModalActionButtonBar } from '../../SharedComponents/ModalActionButtonBar';
import { MultiSelectSinglePhotoDisplay } from '../../SharedComponents/MultiSelectSinglePhotoDisplay';
import { RemovalConfirmationPopup } from '../../SharedComponents/RemovalConfirmationPopup';
import { SortDropdownContained } from '../../SharedComponents/SortDropdownContained';
import { IGetAllPhotosForCurrentUserMeta, IUserPhoto } from '../../Stores/UserPhotoStore';
import { Routes } from '../../_controllers/RouteController';
import { ApplicationStore } from '../../_stores/ApplicationStore';
import { waitForSeconds } from '../../util/waitForSeconds';
import { StoryPhotoUploader } from './StoryPhotoUploader';

interface IState {
    activeTab: Tab;
    selectedPhotoIds: Array<number>;
    showExitConfirm: boolean;
    orderBy: OrderBy;
    successfulPhotoUpload: boolean;
    uploadedPhotos: Array<any>;
    navigationConfirm?: Tab;
}

interface IProps {
    store: ApplicationStore;
}

enum OrderBy {
    CreatedNewestFirst = 'Newest',
    CreatedOldestFirst = 'Oldest',
}

@inject('store')
@observer
export class AddPhotosToStory extends React.Component<RouteComponentProps<any> & IProps, IState> {
    public state: IState = {
        activeTab: Tab.LEFT,
        selectedPhotoIds: [],
        showExitConfirm: false,
        orderBy: OrderBy.CreatedNewestFirst,
        successfulPhotoUpload: false,
        uploadedPhotos: [],
        navigationConfirm: undefined,
    };

    private storyID = parseInt(this.props.match.params.id);

    private onClickPhoto(id: number): void {
        const selectedPhotoIds = [...this.state.selectedPhotoIds];
        const foundIndex = this.state.selectedPhotoIds.findIndex(i => i === id);
        if (foundIndex === -1) {
            selectedPhotoIds.push(id);
        } else {
            selectedPhotoIds.splice(foundIndex, 1);
        }
        this.setState({ selectedPhotoIds });
    }

    private onPressSave(): void {
        this.setState({ successfulPhotoUpload: true });
        this.props.store.storyStore
            .addPhotosToStory(parseInt(this.props.match.params.id), this.state.selectedPhotoIds)
            .then(() => {
                waitForSeconds(1.5).then(() => {
                    this.closeWindow(this.state.selectedPhotoIds);
                });
            });
    }

    private renderChoosePhotos() {
        const photosInStory = this.props.store.storyStore.getStoryByID(parseInt(this.props.match.params.id));
        if (!photosInStory) {
            return;
        }

        const {loadingPhotos, loadingPhotosError, initialPhotosAreLoaded} = this.props.store.userPhotoStore;

        let photos = this.props.store.userPhotoStore.userPhotos
            .filter(p => !photosInStory.photosWithComments.some(obj => obj.photo.id === p.id));

        return (
            <>
                { !initialPhotosAreLoaded ? (
                    <div style={styles.loadingBackground}>
                        <p style={styles.loadingFont}>Loading Photos...</p>
                    </div>
                ) : (
                    <>
                        {loadingPhotosError ? (
                            <div style={styles.loadingBackground}>
                                <p style={styles.loadingFont}>
                                    There was a problem loading your photos. Please&nbsp;
                                    <a
                                        href="/"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            this.props.store.userPhotoStore.getAllPhotosFromServer();
                                        }}
                                    >try again
                                    </a>.
                                </p>
                                <p>
                                    If the problem persists, please <a href="https://tightknit.freshdesk.com/support/tickets/new" target="_blank" rel="noopener">contact support</a>.
                                </p>
                            </div>
                        ) : (
                            <div id="scrollContainer" style={styles.infiniteScrollContainer}>
                                <InfiniteScroll
                                    initialLoad={true}
                                    loadMore={this.loadMore}
                                    hasMore={this.hasMore()}
                                    loader={
                                        <FullContainer key={0} style={{...styles.loadingBackground, paddingTop: 40, paddingBottom: 20}}>
                                            <p style={styles.loadingFont}>Loading More...</p>
                                        </FullContainer>
                                    }
                                    useWindow={false}
                                    style={styles.photosContainer}
                                >
                                    {photos.map((img, index) => {
                                        const photoIsSelected = this.state.selectedPhotoIds.some(i => i === img.id);

                                        return (
                                            <MultiSelectSinglePhotoDisplay
                                                key={index}
                                                onClickPhoto={() => this.onClickPhoto(img.id)}
                                                onClickCheckMark={() => this.onClickPhoto(img.id)}
                                                url={img.thumbnail_url || img.url}
                                                photoIsSelected={photoIsSelected}
                                                photoHeight={250}
                                                imageStyle={styles.pointerCursor}
                                                overrideHover={true}
                                            />
                                        );
                                    })}
                                </InfiniteScroll>
                            </div>
                        )}
                    </>
                )}
            </>
        );
    }

    private onPressExit(): void {
        if (this.state.uploadedPhotos.length > 0) {
            this.setState({ showExitConfirm: true });
        } else {
            this.closeWindow();
        }
    }

    private comparePhotosForSort(): (a: IUserPhoto, b: IUserPhoto) => number {
        switch (this.state.orderBy) {
            case OrderBy.CreatedNewestFirst:
                return (a, b) => moment(b.created_at).diff(moment(a.created_at));
            case OrderBy.CreatedOldestFirst:
                return (a, b) => moment(a.created_at).diff(moment(b.created_at));
            default:
                throw new Error(`Invalid sort order: ${this.state.orderBy}`);
        }
    }

    private closeWindow = (addedPhotoIds?: Array<number>): void => {
        if (this.props.location.state && this.props.location.state.cameFromPhotoViewer) {
            let photoIdText = '';
            if (addedPhotoIds) {
                photoIdText = addedPhotoIds[0].toString(10);
            }
            this.props.store.storyStore.getAllStories().then(() => {
                this.props.history.push(
                    Routes.PHOTO_VIEWER.replace(':id', this.props.match.params.id).replace(':photoID?', photoIdText),
                );
            });
        } else {
            if (this.storyID && addedPhotoIds) {
                this.props.store.storyStore.getAllStories().then(() => {
                    this.props.history.push(`/viewstory/${this.storyID}/`);
                });
            } else if (this.props.location.state && this.props.location.state.emptyStory) {
                this.props.history.push(`/albums/`);
            } else {
                this.props.history.push(`/viewstory/${this.storyID}/`);
            }
        }
    }

    private onClickTab = async (tab: Tab) => {
        if (tab === Tab.RIGHT && this.state.uploadedPhotos.length !== 0) {
            this.setState({ navigationConfirm: tab });
        } else {
            this.setState({ activeTab: tab });
        }
        if (tab === Tab.RIGHT) {
            if (this.props.store.userPhotoStore && !this.props.store.userPhotoStore.initialPhotosAreLoaded) {
                await this.fetchPhotos(false);
            }
        }
    }

    private hasMore = () => {
        return this.props.store && !this.props.store.userPhotoStore.allPhotosLoaded;
    }

    private handleOrderByChange = (orderBy: OrderBy) => {
        this.setState({ orderBy: orderBy as OrderBy });
        const {getAllPhotosForCurrentUserMeta, setGetAllPhotosForCurrentUserMeta} = this.props.store.userPhotoStore;
        let newMeta = {
            page: 0,
        } as IGetAllPhotosForCurrentUserMeta;
        if (orderBy === OrderBy.CreatedNewestFirst) {
            newMeta.sort = 'DESC';
        } else {
            newMeta.sort = 'ASC';
        }
        this.props.store.userPhotoStore.setPhotosPageSort(orderBy);
        this.props.store.userPhotoStore.setGetAllPhotosForCurrentUserMeta(newMeta);
    }

    private loadMore = (page: number) => {
        console.log('load more');
        console.log('page', page);
        if (this.props.store) {
            console.log('have store');
            const {loadingPhotos, allPhotosLoaded} = this.props.store.userPhotoStore;
            console.log('loadingPhotos', loadingPhotos);
            if (!loadingPhotos && !allPhotosLoaded) {
                this.fetchPhotos(false);
            }
        }
    }

    private fetchPhotos = async (hideSpinner: boolean) => {
        if (!this.props.store.userPhotoStore.loadingPhotos) {
            await this.props.store.userPhotoStore.getAllPhotosFromServer(hideSpinner);
        } else {
            console.log('already loading');
        }
    }

    public render() {
        return (
            <FullScreenModalContainer {...this.props} onPressExit={() => this.onPressExit()}>
                <div style={styles.headerContainer}>
                    <div style={styles.tabContainer} onClick={() => this.onClickTab(Tab.LEFT)}>
                        <p style={styles.tabFont(this.state.activeTab === Tab.LEFT)}>{ui.UPLOAD}</p>
                        {this.state.activeTab === Tab.LEFT && <div style={styles.activeBar} />}
                    </div>

                    <div style={styles.tabContainer} onClick={() => this.onClickTab(Tab.RIGHT)}>
                        <p style={styles.tabFont(this.state.activeTab === Tab.RIGHT)}>{ui.YOUR_PHOTOS}</p>
                        {this.state.activeTab === Tab.RIGHT && <div style={styles.activeBar} />}
                    </div>
                </div>

                {this.state.activeTab === Tab.LEFT ? (
                    <StoryPhotoUploader
                        {...this.props}
                        onPhotosChanged={(photos: Array<any>) => this.setState({ uploadedPhotos: photos })}
                        onAfterPhotosAdded={this.closeWindow}
                    />
                ) : (
                    this.renderChoosePhotos()
                )}
                {this.state.activeTab === Tab.RIGHT && (
                    <div style={styles.dropdownContainer}>
                        <SortDropdownContained
                            options={[OrderBy.CreatedNewestFirst, OrderBy.CreatedOldestFirst]}
                            value={this.props.store.userPhotoStore.photosPageSort}
                            onChange={this.handleOrderByChange}
                            style={styles.dropdownBackground}
                        />
                    </div>
                )}
                {this.state.activeTab === Tab.RIGHT && (
                    <ModalActionButtonBar
                        style={styles.actionBar}
                        altPadding={25}
                        confirmOnClick={() => this.onPressSave()}
                        confirmContent={ui.ADD_PHOTOS}
                    />
                )}

                {this.state.showExitConfirm && (
                    <RemovalConfirmationPopup
                        headerText={ui.EXIT_HEADER}
                        subHeaderText={ui.EXIT_SUB_HEADER}
                        cancelText={ui.EXIT_CANCEL}
                        removeText={ui.EXIT_CONFIRM}
                        onClickCancel={() => this.setState({ showExitConfirm: false })}
                        onClickRemove={() => this.closeWindow()}
                    />
                )}

                {this.state.successfulPhotoUpload && (
                    <ExitableAlert
                        headerText={ui.SUCCESS_UPLOAD(
                            this.state.selectedPhotoIds.length,
                            this.props.store.storyStore.getStoryByID(this.storyID).title,
                        )}
                    />
                )}

                {this.state.navigationConfirm && (
                    <RemovalConfirmationPopup
                        headerText={ui.WARNING}
                        subHeaderText={ui.CONFIRM}
                        cancelText={ui.GO_BACK}
                        removeText={ui.CONTINUE}
                        onClickCancel={() => this.setState({ navigationConfirm: undefined })}
                        onClickRemove={() =>
                            this.setState({ activeTab: this.state.navigationConfirm!, navigationConfirm: undefined })
                        }
                    />
                )}
            </FullScreenModalContainer>
        );
    }
}

const styles = {
    dropdownBackground: {
        backgroundColor: '#00000011',
    },
    dropdownContainer: {
        position: 'absolute',
        top: 66,
        right: -8,
        width: 200,
        height: 50,
    } as React.CSSProperties,
    pointerCursor: {
        cursor: 'pointer',
    },
    actionBar: {
        position: 'absolute',
        bottom: 0,
    } as React.CSSProperties,
    infiniteScrollContainer: {
        width: '100%',
        height: '100%',
        overflowY: 'scroll',
        marginBottom: 120,
    } as React.CSSProperties,
    photosContainer: {
        position: 'relative',
        display: 'flex',
        flexWrap: 'wrap',
        flex: 1,
    } as React.CSSProperties,
    tabContainer: {
        height: '100%',
        width: 190,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
    } as React.CSSProperties,
    tabFont: (isActive: boolean) => {
        return {
            width: '100%',
            textAlign: 'center',
            marginTop: 60,
            fontSize: 18,
            ...(isActive ? { fontWeight: 900, opacity: 1 } : { fontWeight: 200, opacity: 0.7 }),
        } as React.CSSProperties;
    },
    activeBar: {
        height: 4,
        width: '100%',
        backgroundColor: '#00A59B',
    },
    headerContainer: {
        height: 125,
        width: '100%',
        borderBottom: '1px solid #E5E5E5',
        display: 'flex',
        justifyContent: 'center',
        flexShrink: 0,
    } as React.CSSProperties,
    loadingFont: {
        fontSize: 20,
        marginBottom: 10,
    },
    loadingBackground: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        paddingTop: 200,
    } as React.CSSProperties,
};



// WEBPACK FOOTER //
// ./src/Pages/StoriesPage/AddPhotosToStory.tsx